import cn from 'classnames'
import {
  type Dispatch,
  type KeyboardEvent,
  type ReactNode,
  type SetStateAction,
  useEffect,
  useRef,
  useState
} from 'react'
import DatePicker, { type DatePickerProps } from 'react-datepicker'
import PhoneInputImport from 'react-phone-input-2'

// react-phone-input-2 is a webpack-bundled CJS module: `module.exports = { __esModule: true, default: Component }`.
// Vite 8's CJS interop re-exports the whole namespace as default instead of unwrapping `.default`,
// so the default import is `{ default: Component }` (object) — React then throws "type is invalid".
const PhoneInput = (PhoneInputImport as unknown as { default?: typeof PhoneInputImport }).default ?? PhoneInputImport

import './coreInput.scss'

import ClosedEyeIcon from '@assets/icons/eye-closed.svg?react'
import OpenedEyeIcon from '@assets/icons/eye-opened.svg?react'
import ClearIcon from '@images/icons/clear.svg?react'
import { tr } from 'mmfintech-commons'

interface InputProps {
  className?: string
  error?: any
  LeftIcon?: ReactNode
  RightIcon?: ReactNode
  label?: ReactNode
  name?: string
  onChange?: any
  hint?: string
  value?: any //string | number
  required?: boolean
  type?: 'text' | 'textarea' | 'password' | 'phone' | 'date' | 'email' | 'number'
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode']
  rows?: number
  clearable?: boolean
  setFocus?: Dispatch<SetStateAction<boolean>>
  focused?: boolean
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void
  onPaste?: (event: React.ClipboardEvent<HTMLInputElement>) => void
  onFocus?: () => void
  disabled?: boolean
  onClick?: () => void
  focusedInput?: (name) => void
  passwordLock?: (boolean) => void
  validation?: (string) => string
  rangeDates?: boolean
  startDate?: any
  endDate?: any
  minDate?: any
  maxDate?: any
  dateFormat?: string
  selectsStart?: boolean
  selectsEnd?: boolean
  placeholder?: string
  maxLength?: number
  autoComplete?: 'off' | 'on'
  size?: 'small' | 'normal' | 'large'
  onBlur?: (event) => void
  readOnly?: boolean
  dataPickerForwardYears?: number
  popperPlacement?: DatePickerProps['popperPlacement']
  blockBlur?: boolean
  enableAutofill?: boolean
}

const isIphoneDevice = () => {
  const userAgent = navigator.userAgent
  return /iPhone|iPad|iPod/i.test(userAgent)
}

const prepareYears = date => {
  return Array(date.getUTCFullYear() - (date.getUTCFullYear() - 110))
    .fill('')
    .map((_, idx) => date.getUTCFullYear() - idx)
}

const minYear = new Date(1900, 0, 1)
const maxYear = new Date(2100, 11, 31)

export const CoreInput = ({
  className = '',
  error,
  LeftIcon,
  RightIcon,
  label,
  name,
  onChange,
  hint,
  value,
  required,
  type = 'text',
  clearable,
  setFocus,
  focused,
  onKeyDown,
  onPaste,
  onFocus,
  disabled,
  rangeDates,
  startDate,
  endDate,
  minDate,
  maxDate,
  dateFormat,
  selectsStart,
  selectsEnd,
  placeholder = '',
  focusedInput,
  passwordLock,
  validation,
  autoComplete = 'off',
  size = 'normal',
  onBlur,
  blockBlur = false,
  dataPickerForwardYears = 0,
  popperPlacement,
  enableAutofill = true,
  readOnly = false,
  ...rest
}: InputProps) => {
  const inputRef = useRef(null)
  const sectionRef = useRef<HTMLDivElement>(null)

  const [inputValue, setInputValue] = useState(value)
  const [isFocused, setIsFocused] = useState(false)
  const [inputType, setInputType] = useState(type)
  const [tempDisable, setTempDisable] = useState(isIphoneDevice())

  const months = [
    tr('METADATA.MONTHS.JAN', 'January'),
    tr('METADATA.MONTHS.FEB', 'February'),
    tr('METADATA.MONTHS.MAR', 'March'),
    tr('METADATA.MONTHS.APR', 'April'),
    tr('METADATA.MONTHS.MAY', 'May'),
    tr('METADATA.MONTHS.JUN', 'June'),
    tr('METADATA.MONTHS.JUL', 'July'),
    tr('METADATA.MONTHS.AUG', 'August'),
    tr('METADATA.MONTHS.SEP', 'September'),
    tr('METADATA.MONTHS.OCT', 'October'),
    tr('METADATA.MONTHS.NOV', 'November'),
    tr('METADATA.MONTHS.DEC', 'December')
  ]

  const date = new Date()
  date.setFullYear(new Date().getFullYear() + dataPickerForwardYears)

  const years = prepareYears(date)

  const getYear = data => {
    return new Date(data).getUTCFullYear()
  }

  const getMonth = data => {
    return new Date(data).getMonth()
  }

  useEffect(() => {
    if (!value) {
      setInputValue(null)
    }
    if (value) {
      setInputValue(validation ? validation(value) : value)
    }
  }, [value])

  useEffect(() => {
    // moves caret to the end of the input as the default focus sets it at the start
    if (inputRef.current && inputType !== 'email' && inputType !== 'number') {
      inputRef.current.selectionStart = inputRef.current.value.length
      inputRef.current.selectionEnd = inputRef.current.value.length
    }
  }, [inputType])

  let timeoutId = null

  useEffect(() => {
    if (focused) {
      setIsFocused(true)
      inputRef?.current?.focus()
    } else {
      setIsFocused(false)
      typeof focusedInput === 'function' && focusedInput(undefined)
      inputRef?.current?.blur()
    }

    return () => {
      clearTimeout(timeoutId)
    }
  }, [focused, blockBlur])

  useEffect(() => {
    if (passwordLock) {
      inputType === 'password' ? passwordLock(true) : passwordLock(false)
    }
  }, [inputType])

  // useEffect(() => {
  //   if (type === 'date' && value !== undefined && value) {
  //     setYears(prepareYears(new Date(value)))
  //   }
  // }, [value])

  //this prevent IOS (Iphone) to focus and blur input fields on render
  useEffect(() => {
    if (isIphoneDevice())
      setTimeout(() => {
        setTempDisable(false)
      }, 200)
    else {
      setTempDisable(false)
    }
  }, [isIphoneDevice()])

  return (
    <div
      onClick={() => {
        setIsFocused(true)
        typeof setFocus === 'function' && setFocus(true)
        inputRef?.current?.focus()
      }}
      className={cn(className, 'core-input-wrapper', {
        'disabled-input': tempDisable || disabled
      })}
    >
      <div
        ref={sectionRef}
        className={cn('core-input-section', { focused: !readOnly && isFocused, errored: !!error }, size)}
      >
        {LeftIcon && <div className='core-input-left-icon'>{LeftIcon}</div>}
        <div
          className={cn('core-input-inner-section', {
            'inner-section-phone': type === 'phone',
            'inner-section-final-state': label && ((!readOnly && isFocused) || !!inputValue)
          })}
        >
          {type === 'phone' ? (
            <PhoneInput
              data-test='phone-input'
              placeholder=''
              onFocus={() => {
                setIsFocused(true)
                typeof focusedInput === 'function' && focusedInput(name)
                typeof setFocus === 'function' && setFocus(true)
              }}
              onBlur={e => {
                timeoutId = setTimeout(() => {
                  setIsFocused(false)
                  setFocus && !blockBlur && setFocus(false)
                  typeof onBlur === 'function' && onBlur(e)
                }, 200)
              }}
              onKeyDown={e => {
                if ((Number.isNaN(Number(e.key)) && e.key !== 'Backspace' && e.key !== 'Tab') || e.key === ' ') {
                  return e.preventDefault()
                }
              }}
              onChange={(value, data: any) => {
                const dialCode = data?.dialCode || ''
                const currentPhoneCode = value.slice(0, dialCode.length)
                if (isFocused) {
                  if (dialCode !== currentPhoneCode) {
                    setInputValue(dialCode)
                    typeof onChange === 'function' && onChange(name, dialCode)
                  } else {
                    setInputValue(value)
                    typeof onChange === 'function' && onChange(name, value)
                  }
                } else {
                  setInputValue('')
                }
              }}
              value={`${inputValue}` || ''}
              disabled={tempDisable || disabled}
              enableSearch
              disableSearchIcon
              searchPlaceholder=''
              inputProps={{ id: name, name, required }}
              masks={{ ci: '.. .. .. .. ..' }}
            />
          ) : type === 'date' ? (
            <div className='core-input-date-picker-wrapper'>
              {!rangeDates ? (
                <DatePicker
                  data-test='date-pick-input'
                  selected={value ? new Date(value) : null}
                  renderCustomHeader={({ date, changeYear, changeMonth }) => (
                    <div className='calendar-header-wrapper'>
                      <select
                        className='calendar-select-input'
                        value={getYear(date)}
                        onChange={({ target: { value } }) => changeYear(value as any)}
                      >
                        {years.map(option => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>

                      <select
                        className='calendar-select-input'
                        value={months[getMonth(date)]}
                        onChange={({ target: { value } }) => changeMonth(months.indexOf(value))}
                      >
                        {months.map(option => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  calendarStartDay={1}
                  onChange={date => onChange && date && onChange(name, date)}
                  calendarClassName='calendar-wrapper-classname'
                  popperTargetRef={sectionRef}
                  popperPlacement={popperPlacement}
                  minDate={minDate ? new Date(minDate) : minYear}
                  maxDate={maxDate ? new Date(maxDate) : maxYear}
                  dateFormat={dateFormat}
                  strictParsing
                  onBlur={e => {
                    timeoutId = setTimeout(() => {
                      if (document.activeElement !== inputRef.current) {
                        setIsFocused(false)
                        setFocus && !blockBlur && setFocus(false)
                        typeof onBlur === 'function' && onBlur(e)
                      }
                    }, 200)
                  }}
                  customInput={
                    <input
                      className={cn('core-input-field', { errored: !!error })}
                      onChange={e => {
                        setInputValue(e.target.value)
                        typeof onChange === 'function' && onChange(name, e.target.value, e)
                      }}
                      value={inputValue || ''}
                      onFocus={() => {
                        if (document.activeElement === inputRef.current) {
                          setIsFocused(true)
                          typeof focusedInput === 'function' && focusedInput(name)
                          typeof setFocus === 'function' && setFocus(true)
                        }
                      }}
                      type={'text'}
                      disabled={tempDisable || disabled}
                      autoComplete='off'
                      ref={inputRef}
                      required={required}
                      name={name}
                      onKeyDown={onKeyDown}
                      onClick={rest.onClick}
                      maxLength={rest.maxLength}
                    />
                  }
                />
              ) : (
                <DatePicker
                  renderCustomHeader={({ date, changeYear, changeMonth }) => (
                    <div className='calendar-header-wrapper'>
                      <select
                        className='calendar-select-input'
                        value={getYear(date)}
                        onChange={({ target: { value } }) => changeYear(value as any)}
                      >
                        {years.map(option => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                      <select
                        className='calendar-select-input'
                        value={months[getMonth(date)]}
                        onChange={({ target: { value } }) => {
                          changeMonth(months.indexOf(value))
                        }}
                      >
                        {months.map(option => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  data-test='date-pick-input'
                  selected={inputValue ? new Date(inputValue as string | number) : null}
                  onChange={(date: Date | null) => {
                    if (date) {
                      setInputValue(date.toISOString())
                    }
                    onChange && date && onChange(name, date)
                  }}
                  startDate={startDate ? new Date(startDate) : null}
                  endDate={endDate ? new Date(endDate) : null}
                  selectsStart={selectsStart}
                  minDate={minDate ? new Date(minDate) : minYear}
                  maxDate={maxDate ? new Date(maxDate) : maxYear}
                  dateFormat={dateFormat}
                  strictParsing
                  selectsEnd={selectsEnd}
                  calendarClassName={'calendar-wrapper-classname'}
                  popperTargetRef={sectionRef}
                  popperPlacement={popperPlacement}
                  onBlur={e => {
                    timeoutId = setTimeout(() => {
                      if (document.activeElement !== inputRef.current) {
                        setIsFocused(false)
                        setFocus && !blockBlur && setFocus(false)
                        typeof onBlur === 'function' && onBlur(e)
                      }
                    }, 200)
                  }}
                  customInput={
                    <input
                      className={cn('core-input-field', { errored: !!error })}
                      onChange={e => {
                        setInputValue(e.target.value)
                        typeof onChange === 'function' && onChange(name, e.target.value, e)
                      }}
                      value={inputValue as string}
                      onFocus={() => {
                        if (document.activeElement === inputRef.current) {
                          setIsFocused(true)
                          typeof focusedInput === 'function' && focusedInput(name)
                          typeof setFocus === 'function' && setFocus(true)
                        }
                      }}
                      type={'text'}
                      disabled={tempDisable || disabled}
                      ref={inputRef}
                      required={required}
                      name={name}
                      onKeyDown={onKeyDown}
                      autoComplete='off'
                      onClick={rest.onClick}
                      maxLength={rest.maxLength}
                    />
                  }
                />
              )}
            </div>
          ) : (
            <input
              data-test='base-input'
              className={cn('core-input-field', { errored: !!error })}
              onChange={e => {
                if (isFocused || enableAutofill) {
                  setInputValue(e.target.value)
                  typeof onChange === 'function' && onChange(name, e.target.value, e)
                }
              }}
              value={value || inputValue || ''}
              onFocus={() => {
                if (document.activeElement === inputRef.current) {
                  setIsFocused(true)
                  typeof focusedInput === 'function' && focusedInput(name)
                  typeof setFocus === 'function' && setFocus(true)
                  typeof onFocus === 'function' && onFocus()
                }
              }}
              onBlur={e => {
                timeoutId = setTimeout(() => {
                  if (document.activeElement !== inputRef.current) {
                    setIsFocused(false)
                    !blockBlur && setFocus && setFocus(false)
                    typeof onBlur === 'function' && onBlur(e)
                  }
                }, 200)
              }}
              type={inputType}
              disabled={tempDisable || disabled}
              ref={inputRef}
              required={required}
              name={name}
              onKeyDown={onKeyDown}
              onPaste={onPaste}
              // With a floating label the placeholder only shows on focus (so it doesn't clash
              // with the resting label). With no label, the field relies on the placeholder as its
              // only hint, so show it at rest too.
              placeholder={!readOnly && (isFocused || !label) ? placeholder : ''}
              autoComplete={autoComplete}
              readOnly={readOnly}
              {...rest}
            />
          )}
          {label && (
            <span
              className={cn('core-input-moving-label', {
                'initial-state': !isFocused && !inputValue,
                'final-state': isFocused || inputValue || readOnly
              })}
            >
              {label}
              {required && <span className='required'>*</span>}
            </span>
          )}
        </div>
        <div className='core-input-icons-section'>
          {clearable && isFocused && inputValue && (
            <ClearIcon
              onClick={() => {
                setInputValue('')
                inputRef?.current?.focus()
                typeof onChange === 'function' && onChange(name, '')
              }}
            />
          )}
          {RightIcon ||
            (type === 'password' ? (
              inputType === 'password' ? (
                <OpenedEyeIcon
                  onClick={() => {
                    setInputType('text')
                    inputRef?.current?.focus()
                  }}
                />
              ) : (
                <ClosedEyeIcon
                  onClick={() => {
                    setInputType('password')
                    inputRef?.current?.focus()
                  }}
                />
              )
            ) : null)}
        </div>
      </div>
      {error && (
        <p data-test='core-input-error' className='core-input-error caption mt-1 pl-2'>
          {error}
        </p>
      )}
      {hint && !error && (
        <p data-test='core-input-hint' className='caption-medium core-input-hint mt-1 pl-2'>
          {hint}
        </p>
      )}
    </div>
  )
}
